<template>
    <section class="third-custom-fields">
        <h4 v-if="hasLabel" class="gray-60 m-0 mt-3 mb-2">
            {{ $l10n.__('%s - CHAMPS PERSONNALISÉS OBLIGATOIRES', typeLabel) | upperCase }}
        </h4>

        <!-- CF Group loop -->
        <template v-for="(group, groupIndex) in groupedCustomFields">
            <!-- Fields in group -->
            <article v-if="group.id !== 0" :key="group.id" :class="{ 'mt-5': groupIndex !== 0 }">
                <header>
                    <h6 class="semibold gray-60 fs-3">{{ group.name | upperCase }}</h6>
                </header>
                <!-- Field -->
                <el-form-item
                    v-for="(field, index) in group.fields"
                    :key="field.id"
                    :prop="`groupedCustomFields.${groupIndex}.fields.${index}.value`"
                    :label="field.name"
                    :ref="`groupedCustomFields.${groupIndex}.fields.${index}.value`"
                    :rules="field.rules"
                >
                    <template slot="label">
                        <span>
                            {{ field.name }}
                        </span>
                        <el-tooltip
                            v-if="field.description.length"
                            class="pabsolute right--4 top-1"
                            effect="dark"
                            popper-class="form-fields-customfields-tool-tip"
                            placement="bottom-start"
                        >
                            <div slot="content" v-html="field.description"></div>
                            <i class="icon-rounded-question-fillout"></i>
                        </el-tooltip>
                    </template>
                    <component
                        :is="'cf-' + field.type"
                        :parameters="field.parameters"
                        :value="field.value"
                        :type="field.type"
                        :id="field.id"
                        @input="updateValue"
                    />
                </el-form-item>
            </article>

            <!-- Fields without group -->
            <el-form-item
                v-else
                v-for="(field, index) in group.fields"
                :ref="`groupedCustomFields-${groupIndex}-fields-${index}-value`"
                :key="field.id"
                :prop="`groupedCustomFields.${groupIndex}.fields.${index}.value`"
                :label="field.name"
                :rules="field.rules"
            >
                <template slot="label">
                    <span>
                        {{ field.name }}
                    </span>
                    <el-tooltip
                        v-if="field.description.length"
                        class="pabsolute right--4 top-1"
                        effect="dark"
                        placement="bottom-start"
                    >
                        <div slot="content" v-html="field.description"></div>
                        <i class="icon-rounded-question-fillout"></i>
                    </el-tooltip>
                </template>
                <component
                    :is="'cf-' + field.type"
                    :parameters="field.parameters"
                    :value="field.value"
                    :type="field.type"
                    :id="field.id"
                    @input="updateValue"
                />
            </el-form-item>
        </template>
        <CompassButton
            :label="$t.__('thirds.thirdscomponents.FormFieldsCustomFields.add_custom_field')"
            type="secondary"
            :disabled="!available"
            :data-bot="getDatabot('add-cf', currentStore, 'cf')"
            @click="showCustomFieldsModal = true"
            class="wp-10 mt-2"
            v-if="allowAdd"
        >
            <template slot="prefix" v-if="!available">
                <el-tooltip
                    class="pabsolute form-fields-custom-fields-el-tooltip"
                    :content="
                        $t.__(
                            'thirds.thirdscomponents.FormFieldsCustomFields.no_custom_field_available',
                        )
                    "
                    effect="dark"
                    placement="top"
                >
                    <div class="pabsolute wp-10"></div>
                </el-tooltip>
            </template>
        </CompassButton>
        <custom-fields-modal
            ref="customFieldsModal"
            :visible="showCustomFieldsModal"
            :currentStore="currentStore"
            :customFields="customfields"
            @close="showCustomFieldsModal = false"
        >
        </custom-fields-modal>
    </section>
</template>

<script>
import DataBotsFormsMixins from 'mixins/DataBotsFormsMixins';

import SimpleText from 'vcomponents/inputs/customfields/SimpleText.vue';
import CfAmount from 'vcomponents/inputs/customfields/Amount.vue';
import CfBoolean from 'vcomponents/inputs/customfields/Boolean.vue';
import CfCheckbox from 'vcomponents/inputs/customfields/Checkbox.vue';
import cfDate from 'vcomponents/inputs/customfields/Date.vue';
import Email from 'vcomponents/inputs/customfields/Email.vue';
import Item from 'vcomponents/inputs/customfields/Item.vue';
import Numeric from 'vcomponents/inputs/customfields/Numeric.vue';
import Contact from 'vcomponents/inputs/customfields/Contact.vue';
import Radios from 'vcomponents/inputs/customfields/Radio.vue';
import Select from 'vcomponents/inputs/customfields/Select.vue';
import Staff from 'vcomponents/inputs/customfields/Staff.vue';
import Third from 'vcomponents/inputs/customfields/Third.vue';
import Time from 'vcomponents/inputs/customfields/Time.vue';
import RichText from 'vcomponents/inputs/customfields/RichText.vue';
import Unit from 'vcomponents/inputs/customfields/Unit.vue';
import Url from 'vcomponents/inputs/customfields/Url.vue';

import CustomFieldsModal from 'vcomponents/thirds/thirdscomponents/CustomFieldsModal.vue';

export default {
    props: {
        typeLabel: {
            type: String,
            required: false,
            default: null,
        },
        currentStore: {
            type: String,
            required: true,
        },
        currentCustomfields: {
            type: Array,
            required: false,
            default: () => [],
        },
        allowAdd: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    mixins: [DataBotsFormsMixins],
    data() {
        return {
            showCustomFieldsModal: false,
        };
    },
    created() {
        this.fetchCustomFieldsByType(this.$store.getters[`${this.currentStore}/getType`]).then(
            () => {
                if (this.currentCustomfields && this.currentCustomfields.length > 0) {
                    this.$store.dispatch(
                        `${this.currentStore}/fetchCustomFields`,
                        this.currentCustomfields,
                    );
                    this.currentCustomfields.forEach((cfs) => {
                        this.$refs.customFieldsModal.handleFieldsUpdated({
                            id: cfs.customfield_group.id,
                            fields: cfs,
                        });
                    });
                }
            },
        );
    },
    computed: {
        customfields() {
            return this.currentCustomfields && this.currentCustomfields.length > 0
                ? this.currentCustomfields
                : [];
        },
        groupedCustomFields() {
            return this.$store.getters[`${this.currentStore}/groupedCustomFields`];
        },
        hasLabel() {
            return !_.isNil(this.typeLabel) && this.groupedCustomFields.length > 0;
        },
        available() {
            return this.$store.getters[`${this.currentStore}/availablesCustomFields`]?.length > 0;
        },
    },
    methods: {
        updateValue(value) {
            this.$store.dispatch(`${this.currentStore}/updateOneCustomField`, value);
        },
        fetchCustomFieldsByType(type) {
            return this.$store.dispatch(`${this.currentStore}/fetchCustomFieldsByType`, type);
        },
    },
    components: {
        'cf-simple-text': SimpleText,
        'cf-amount': CfAmount,
        'cf-boolean': CfBoolean,
        'cf-checkbox': CfCheckbox,
        'cf-date': cfDate,
        'cf-email': Email,
        'cf-item': Item,
        'cf-numeric': Numeric,
        'cf-contact': Contact,
        'cf-radio': Radios,
        'cf-select': Select,
        'cf-staff': Staff,
        'cf-company-individual': Third,
        'cf-time': Time,
        'cf-rich-text': RichText,
        'cf-numeric-with-unit': Unit,
        'cf-url': Url,
        CustomFieldsModal,
    },
};
</script>

<style lang="scss">
.third-custom-fields {
    .el-form-item.is-required .el-form-item__label:after {
        margin-left: 0px;
    }
    .el-form-item__label {
        position: relative;
    }
    .form-fields-custom-fields-el-tooltip {
        left: 0;
        top: 0;
        height: 100%;
    }
}
.form-fields-customfields-tool-tip {
    transform: translateX(-8.5%);
}
</style>
